/* ==========================================================================
   FNDRY — predictive search
   Plain JS, no framework. Backs the header search panel and the mobile
   drawer search with live results from sections/fndry-predictive-search.

   Both hosts are in the DOM at once, so the injected markup's ids are
   rewritten per host — see uniquify(). Nothing here writes storage.
   ========================================================================== */

(function () {
  'use strict';

  var HOST = '[data-fndry-ps]';
  var MIN_CHARS = 2;
  var DEBOUNCE = 250;
  var LIMIT = 5;
  var hostSeq = 0;

  function $(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function $$(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  /* Rewrite every id in an injected fragment so two hosts can hold the same
     markup without colliding, and repoint the aria references that use them. */
  function uniquify(root, prefix) {
    $$('[id]', root).forEach(function (el) {
      el.id = prefix + '-' + el.id;
    });
    $$('[aria-labelledby]', root).forEach(function (el) {
      el.setAttribute(
        'aria-labelledby',
        el
          .getAttribute('aria-labelledby')
          .split(/\s+/)
          .map(function (id) {
            return prefix + '-' + id;
          })
          .join(' ')
      );
    });
    if (root.id) root.id = prefix + '-' + root.id;
  }

  function Predictive(host) {
    this.host = host;
    this.input = $('input[type="search"]', host);
    this.panel = $('[data-fndry-ps-panel]', host);
    if (!this.input || !this.panel) return;

    hostSeq += 1;
    this.prefix = 'fps' + hostSeq;
    this.url = host.dataset.fndryPs || '/search/suggest';
    this.timer = null;
    this.controller = null;
    this.options = [];
    this.activeIndex = -1;
    this.lastTerm = '';

    this.panel.id = this.prefix + '-panel';
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-controls', this.panel.id);
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-haspopup', 'listbox');
    this.input.setAttribute('autocomplete', 'off');
    this.input.setAttribute('autocorrect', 'off');
    this.input.setAttribute('autocapitalize', 'off');
    this.input.setAttribute('spellcheck', 'false');

    this.bind();
  }

  Predictive.prototype.bind = function () {
    var self = this;

    this.input.addEventListener('input', function () {
      window.clearTimeout(self.timer);
      var term = self.input.value.trim();

      if (term.length < MIN_CHARS) {
        self.clear();
        return;
      }

      self.timer = window.setTimeout(function () {
        self.search(term);
      }, DEBOUNCE);
    });

    this.input.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        if (self.host.classList.contains('is-ps-open')) {
          event.stopPropagation();
          self.clear();
        }
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        self.move(1);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        self.move(-1);
        return;
      }
      if (event.key === 'Enter' && self.activeIndex > -1) {
        var link = $('a', self.options[self.activeIndex]);
        if (link) {
          event.preventDefault();
          window.location.href = link.href;
        }
      }
    });

    /* A click inside the results is a navigation, so only a blur that leaves
       the whole host closes the list. */
    this.host.addEventListener('focusout', function () {
      window.setTimeout(function () {
        if (!self.host.contains(document.activeElement)) self.close();
      }, 0);
    });

    this.panel.addEventListener('pointerdown', function (event) {
      var item = event.target.closest ? event.target.closest('[role="option"]') : null;
      if (!item) return;
      var link = $('a', item);
      if (link) window.location.href = link.href;
    });
  };

  Predictive.prototype.search = function (term) {
    var self = this;
    if (term === this.lastTerm && this.panel.innerHTML.trim() !== '') {
      this.open();
      return;
    }
    this.lastTerm = term;

    if (this.controller) this.controller.abort();
    this.controller = typeof AbortController === 'function' ? new AbortController() : null;

    this.host.classList.add('is-ps-loading');

    var url =
      this.url +
      '?q=' +
      encodeURIComponent(term) +
      '&resources[type]=product,page,article,query' +
      '&resources[limit]=' +
      LIMIT +
      '&section_id=fndry-predictive-search';

    fetch(url, this.controller ? { signal: this.controller.signal } : undefined)
      .then(function (response) {
        if (!response.ok) throw new Error('predictive search failed');
        return response.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var section = doc.querySelector('#shopify-section-fndry-predictive-search') || doc.body;
        var results = section.querySelector('.fndry-ps');
        if (!results) {
          self.clear();
          return;
        }
        uniquify(results, self.prefix);
        self.panel.innerHTML = '';
        self.panel.appendChild(results);
        self.collect();
        self.open();
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        self.clear();
      })
      .finally(function () {
        self.host.classList.remove('is-ps-loading');
      });
  };

  Predictive.prototype.collect = function () {
    this.options = $$('[role="option"]', this.panel);
    this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
  };

  Predictive.prototype.move = function (step) {
    if (!this.options.length) return;
    var next = this.activeIndex + step;
    if (next < 0) next = this.options.length - 1;
    if (next >= this.options.length) next = 0;
    this.setActive(next);
  };

  Predictive.prototype.setActive = function (index) {
    this.options.forEach(function (option, i) {
      var on = i === index;
      option.setAttribute('aria-selected', on ? 'true' : 'false');
      option.classList.toggle('is-active', on);
    });
    this.activeIndex = index;
    var option = this.options[index];
    if (option) {
      this.input.setAttribute('aria-activedescendant', option.id);
      if (option.scrollIntoView) option.scrollIntoView({ block: 'nearest' });
    }
  };

  Predictive.prototype.open = function () {
    this.host.classList.add('is-ps-open');
    this.input.setAttribute('aria-expanded', 'true');
  };

  Predictive.prototype.close = function () {
    this.host.classList.remove('is-ps-open');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
  };

  Predictive.prototype.clear = function () {
    window.clearTimeout(this.timer);
    if (this.controller) this.controller.abort();
    this.controller = null;
    this.lastTerm = '';
    this.panel.innerHTML = '';
    this.options = [];
    this.close();
  };

  /* ---------- init ------------------------------------------------------ */

  function init(host) {
    if (!host || host.dataset.fndryPsReady === 'true') return;
    host.dataset.fndryPsReady = 'true';
    new Predictive(host);
  }

  function initAll(scope) {
    $$(HOST, scope || document).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
